import { WallDatumAbstractionProvider } from "building-generation/elements/datums/datumAbstractionProviders";
import { getExteriorRoofWallFaces, getSideRoofWallFaces } from "building-generation/elements/roofs";
import { adjustRoofTopFaceUVs } from "building-generation/elements/roofs/roofExtrusions";
import { createRoofRenderLevelResolver } from "building-generation/elements/roofs/roofRenderLevel";
import {
  collectWallFinishFaces,
  collectWallFinishFaces2,
} from "building-generation/elements/wallAssemblies/wallFinishFaces";
import {
  uvGenerationForRoofCap,
  uvGenerationForRoofTrim,
  uvTransformationForRoofCap,
} from "building-generation/utils/uvGenerationForEntities";
import type { Building } from "building-types/building/buildingTypes";
import {
  BuildingGeometryMode,
  createWrappedEmitter,
  type BuildingGeometryEmitter,
  type BuildingGeometryOptions,
} from "building-types/geometry/buildingGeometryTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import { FaceCategory, wallFaceCategories } from "building-types/materials/faceCategories";
import type { PropAttributeTable } from "building-types/props/propTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofDormer } from "building-types/roofs/roofGenerationTypes";
import type { RoofWallSystem } from "building-types/roofs/roofWallSystemTypes";
import type { HomeSettings } from "building-types/settings/homeSettings";
import { RoofSide, type SkylightUnit } from "building-types/settings/roofTypes";
import { EaveType } from "building-types/settings/settingsTypesEnum";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { materialSrcFromPalette } from "doc/materialLookup";
import { faceCategoryFromPropCategory } from "doc/propTools";
import { ROOF_Z_FIGHTING_OFFSET } from "doc/utils/roofTools";
import type { AABB3 } from "geom/aabb3Types";
import type { Face } from "geom/brep";
import { faceCopy, faceGroupNew, faceMove, facesAABB3 } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { extrusionToBRep } from "geom/extrude";
import { planeNormal } from "geom/plane";
import { UVGeneration } from "geom/uv";
import { buildExteriorLayers, emitBuildingProp, getExteriorGroupsGeometries } from "./buildingGeometryTools";
import type { BuildingForWallGeometries } from "./wallsGeometry";
import { getWallFinishGeometries, getWallSweepGeometries } from "./wallsGeometry";

type BuildingForRoofGeometries = BuildingForWallGeometries &
  Pick<Building, "blocks" | "roofSystems" | "dormers" | "skylightUnits"> & {
    readonly propCatalog?: PropAttributeTable;
  };

type HomeSettingsForRoofGeometries = Pick<HomeSettings, "roof" | "wall" | "wallCatalog">;

/** Material used for the underside of roofs (soffits, bottom faces) */
const ROOF_BOTTOM_MATERIAL: ExteriorMaterial = "trimExterior0";

/**
 * Generate BuildingGeometry for roof trim elements (fascia, frieze, soffits, etc.)
 *
 * This converts FaceArrayViewEntity arrays to BuildingGeometry, handling:
 * - Material resolution from exterior palette
 * - Entity ID and descriptor preservation
 * - UV generation for roof trims
 */
export function getRoofTrimGeometries(
  trims: FaceArrayViewEntity[],
  faceCategory: FaceCategory,
  exteriorPalette: ExteriorPalette,
  entityHandleForSelectingReportingUnit: DocEntityHandle,
  emitter: BuildingGeometryEmitter,
): void {
  const threeLayers = buildExteriorLayers();
  const uvGen = uvGenerationForRoofTrim();

  for (const { faces, descriptor, entityId, material = "trimExterior0", materialTransformation, pickHandle } of trims) {
    if (faces.length === 0) {
      continue;
    }

    const materialSrc = materialSrcFromPalette(exteriorPalette, material);

    emitter.emit({
      geom: faceGroupNew(faces),
      faceCategory,
      threeLayers,
      pickHandle,
      entityId,
      descriptor,
      entityHandleForSelectingReportingUnit,
      materialSlot: material,
      materialSrc,
      uvGen,
      materialTransformation,
    });
  }
}

/**
 * Generate BuildingGeometry for roof ridge vents.
 *
 * Ridge vents are always rendered with the "roofTrim0" material slot.
 */
export function getRoofRidgeVentGeometries(
  ridgeVentFaces: Face[],
  exteriorPalette: ExteriorPalette,
  pickHandle: DocEntityHandle | undefined,
  entityHandleForSelectingReportingUnit: DocEntityHandle,
  emitter: BuildingGeometryEmitter,
): void {
  if (ridgeVentFaces.length === 0) {
    return;
  }

  const threeLayers = buildExteriorLayers();
  const materialSlot: ExteriorMaterial = "roofTrim0";
  const materialSrc = materialSrcFromPalette(exteriorPalette, materialSlot);

  emitter.emit({
    geom: faceGroupNew(ridgeVentFaces),
    faceCategory: FaceCategory.ROOF_RIDGE_VENT,
    threeLayers,
    pickHandle,
    entityHandleForSelectingReportingUnit,
    materialSlot,
    materialSrc,
    uvGen: uvGenerationForRoofCap(),
    materialTransformation: uvTransformationForRoofCap(),
  });
}

/**
 * Generate BuildingGeometry for roof ridge caps.
 *
 * Ridge caps use the roofing cap material (defaults to roofing material).
 */
export function getRoofRidgeCapGeometries(
  ridgeCapFaces: Face[],
  exteriorPalette: ExteriorPalette,
  roofRidgeRoofingCapMaterial: ExteriorMaterial,
  pickHandle: DocEntityHandle | undefined,
  entityHandleForSelectingReportingUnit: DocEntityHandle,
  emitter: BuildingGeometryEmitter,
): void {
  if (ridgeCapFaces.length === 0) {
    return;
  }

  const threeLayers = buildExteriorLayers();
  const materialSrc = materialSrcFromPalette(exteriorPalette, roofRidgeRoofingCapMaterial);

  emitter.emit({
    geom: faceGroupNew(ridgeCapFaces),
    faceCategory: FaceCategory.ROOF_RIDGE_CAP,
    threeLayers,
    pickHandle,
    entityHandleForSelectingReportingUnit,
    materialSlot: roofRidgeRoofingCapMaterial,
    materialSrc,
    uvGen: uvGenerationForRoofCap(),
    materialTransformation: uvTransformationForRoofCap(),
  });
}

interface ClassifiedRoofingFaces {
  readonly topFaces: Face[];
  readonly sideFaces: Face[];
  readonly bottomFaces: Face[];
}

/**
 * Classify faces by their normal direction for named roof export components.
 */
function classifyRoofingFaces(faces: Face[]): ClassifiedRoofingFaces {
  const topFaces: Face[] = [];
  const sideFaces: Face[] = [];
  const bottomFaces: Face[] = [];

  for (const face of faces) {
    const { plane } = face;
    const [, , nZ] = planeNormal(plane);
    if (nZ < -DIST_EPSILON) {
      bottomFaces.push(face);
    } else if (nZ > DIST_EPSILON) {
      topFaces.push(face);
    } else {
      sideFaces.push(face);
    }
  }

  return { topFaces, sideFaces, bottomFaces };
}

function nonBottomRoofingFaces(faces: Face[]): Face[] {
  return faces.filter((face) => {
    const [, , nZ] = planeNormal(face.plane);
    return nZ >= -DIST_EPSILON;
  });
}

function emitRoofingFaces({
  faces,
  faceCategory,
  threeLayers,
  materialSlot,
  materialSrc,
  pickHandle,
  entityHandleForSelectingReportingUnit,
  exportComponentName,
  emitter,
}: {
  faces: Face[];
  faceCategory: FaceCategory.ROOF_TOP | FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY;
  threeLayers: number[];
  materialSlot: ExteriorMaterial;
  materialSrc: ReturnType<typeof materialSrcFromPalette>;
  pickHandle?: DocEntityHandle;
  entityHandleForSelectingReportingUnit: DocEntityHandle;
  exportComponentName: string;
  emitter: BuildingGeometryEmitter;
}): void {
  if (faces.length === 0) {
    return;
  }

  emitter.emit({
    geom: faceGroupNew(faces),
    faceCategory,
    threeLayers,
    materialSlot,
    materialSrc,
    uvGen: UVGeneration.GLOBAL_RECT_U,
    pickHandle,
    entityHandleForSelectingReportingUnit,
    exportComponentName,
  });
}

/**
 * Generate BuildingGeometry for roof top surfaces (the visible roofing material).
 *
 * In render mode:
 * - Extracts only the top face from each extrusion
 * - Applies UV adjustments for proper material mapping
 * - Applies z-offset to prevent z-fighting with trim elements
 * - Uses trimsRoofMat for trim faces
 *
 * In export mode:
 * - Uses extrudedTrimsRoofMat instead of trimsRoofMat
 * - Classifies trim faces by normal direction (top, side, underside)
 * - Bottom faces use ROOF_BOTTOM_MATERIAL
 */
export function getRoofTopGeometries(
  roof: GeneratedRoof,
  exteriorPalette: ExteriorPalette,
  isExporting: boolean,
  entityHandleForSelectingReportingUnit: DocEntityHandle,
  emitter: BuildingGeometryEmitter,
): void {
  const { roofTopPlates, roofSettings, trimsRoofMat, extrudedTrimsRoofMat } = roof;
  const { roofingMaterial = "roof0" } = roofSettings;

  const threeLayers = buildExteriorLayers();

  const pickHandle = roof.pickHandle;

  if (isExporting) {
    // Export mode: use extrudedTrimsRoofMat and classify faces by normal
    for (const { faces, material } of extrudedTrimsRoofMat) {
      const { topFaces, sideFaces, bottomFaces } = classifyRoofingFaces(faces);
      const trimMaterial = material ?? roofingMaterial;
      const topMaterialSrc = materialSrcFromPalette(exteriorPalette, trimMaterial);
      const bottomMaterialSrc = materialSrcFromPalette(exteriorPalette, ROOF_BOTTOM_MATERIAL);

      emitRoofingFaces({
        faces: topFaces,
        faceCategory: FaceCategory.ROOF_TOP,
        threeLayers,
        materialSlot: trimMaterial,
        materialSrc: topMaterialSrc,
        pickHandle,
        entityHandleForSelectingReportingUnit,
        exportComponentName: "Trim/Top",
        emitter,
      });
      emitRoofingFaces({
        faces: sideFaces,
        faceCategory: FaceCategory.ROOF_TOP,
        threeLayers,
        materialSlot: trimMaterial,
        materialSrc: topMaterialSrc,
        pickHandle,
        entityHandleForSelectingReportingUnit,
        exportComponentName: "Trim/Side",
        emitter,
      });
      emitRoofingFaces({
        faces: bottomFaces,
        faceCategory: FaceCategory.ROOF_TOP,
        threeLayers,
        materialSlot: ROOF_BOTTOM_MATERIAL,
        materialSrc: bottomMaterialSrc,
        pickHandle,
        entityHandleForSelectingReportingUnit,
        exportComponentName: "Trim/Underside",
        emitter,
      });
    }
  } else {
    // Render mode: extract top faces and apply UV adjustments
    // Note: Ordering preserved to match original - roofTopPlates first, then trimsRoofMat

    // 1. First output: top faces from roof plates
    const roofPlateFaces: Face[] = [];
    for (let i = 0, l = roofTopPlates.length; i < l; i++) {
      const brepRoofTop = extrusionToBRep(roofTopPlates[i]);
      const [, topFace] = brepRoofTop.faces;

      adjustRoofTopFaceUVs(topFace);

      // Apply z-offset to prevent z-fighting with trims
      faceMove(topFace, 0, 0, ROOF_Z_FIGHTING_OFFSET);

      roofPlateFaces.push(topFace);
    }

    if (roofPlateFaces.length > 0) {
      const materialSrc = materialSrcFromPalette(exteriorPalette, roofingMaterial);
      emitter.emit({
        geom: faceGroupNew(roofPlateFaces),
        faceCategory: FaceCategory.ROOF_TOP,
        threeLayers,
        materialSlot: roofingMaterial,
        materialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_U,
        pickHandle,
        entityHandleForSelectingReportingUnit,
      });
    }

    // 2. Second output: roof-material colored trim faces (trimsRoofMat) - each entry separate
    for (const { faces, material } of trimsRoofMat) {
      const trimMaterial = material ?? roofingMaterial;
      const adjustedFaces = faces.map((face) =>
        faceMove(adjustRoofTopFaceUVs(faceCopy(face)), 0, 0, ROOF_Z_FIGHTING_OFFSET),
      );

      const trimMaterialSrc = materialSrcFromPalette(exteriorPalette, trimMaterial);
      emitter.emit({
        geom: faceGroupNew(adjustedFaces),
        faceCategory: FaceCategory.ROOF_TOP,
        threeLayers,
        materialSlot: trimMaterial,
        materialSrc: trimMaterialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_U,
        pickHandle,
        entityHandleForSelectingReportingUnit,
      });
    }
  }
}

/**
 * Generate BuildingGeometry for roof bottom surfaces (visible from underneath).
 *
 * In render mode:
 * - Extracts bottom and side faces from extrusions (only when eave type requires it)
 * - Includes pre-computed bottom faces from the roof
 *
 * In export mode:
 * - Includes ALL faces from the extrusions (full geometry for export)
 */
export function getRoofBottomGeometries(
  roof: GeneratedRoof,
  exteriorPalette: ExteriorPalette,
  isExporting: boolean,
  entityHandleForSelectingReportingUnit: DocEntityHandle,
  emitter: BuildingGeometryEmitter,
): void {
  const { roofTopPlates, bottomFaces, roofSettings } = roof;
  const { eaveType } = roofSettings;

  const threeLayers = buildExteriorLayers();
  const allBottomFaces: Face[] = [];

  if (isExporting) {
    // Export mode: include all faces from extrusions
    for (let i = 0, l = roofTopPlates.length; i < l; i++) {
      const brepRoofTop = extrusionToBRep(roofTopPlates[i]);
      allBottomFaces.push(...brepRoofTop.faces);
    }
  } else {
    // Render mode: only bottom and side faces when eave type requires it
    for (let i = 0, l = roofTopPlates.length; i < l; i++) {
      const brepRoofTop = extrusionToBRep(roofTopPlates[i]);
      const [bottomFace, , ...sideFaces] = brepRoofTop.faces;

      if (eaveType === EaveType.ANGLED_OPEN_OPEN || roofSettings.includeFasciaTopTrim) {
        allBottomFaces.push(bottomFace);
        allBottomFaces.push(...sideFaces);
      }
    }
  }

  // Always include the pre-computed bottom faces
  allBottomFaces.push(...bottomFaces);

  if (allBottomFaces.length > 0) {
    const materialSrc = materialSrcFromPalette(exteriorPalette, ROOF_BOTTOM_MATERIAL);
    emitter.emit({
      geom: faceGroupNew(allBottomFaces),
      faceCategory: FaceCategory.ROOF_BOTTOM,
      threeLayers,
      materialSlot: ROOF_BOTTOM_MATERIAL,
      materialSrc,
      uvGen: UVGeneration.GLOBAL_RECT_UV,
      entityHandleForSelectingReportingUnit,
    });
  }
}

/**
 * Generate BuildingGeometry for customizable export roof geometry.
 *
 * This is used in export mode to provide separately-customizable top and bottom faces
 * from the roofingMaterialFaces, classified by normal direction.
 */
export function getRoofTopCustomizableExportGeometries(
  roof: GeneratedRoof,
  exteriorPalette: ExteriorPalette,
  entityHandleForSelectingReportingUnit: DocEntityHandle,
  emitter: BuildingGeometryEmitter,
): void {
  const { roofingMaterialFaces, roofSettings } = roof;
  const { roofingMaterial = "roof0" } = roofSettings;

  if (roofingMaterialFaces.length === 0) {
    return;
  }

  const threeLayers = buildExteriorLayers();
  const sideAndTopFaces = nonBottomRoofingFaces(roofingMaterialFaces);
  const bottomFaces = roofingMaterialFaces.filter((face) => {
    const [, , nZ] = planeNormal(face.plane);
    return nZ < -DIST_EPSILON;
  });
  const topMaterialSrc = materialSrcFromPalette(exteriorPalette, roofingMaterial);
  const bottomMaterialSrc = materialSrcFromPalette(exteriorPalette, ROOF_BOTTOM_MATERIAL);

  emitRoofingFaces({
    faces: bottomFaces,
    faceCategory: FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY,
    threeLayers,
    materialSlot: ROOF_BOTTOM_MATERIAL,
    materialSrc: bottomMaterialSrc,
    entityHandleForSelectingReportingUnit,
    exportComponentName: "Main/Underside",
    emitter,
  });

  // Keep top and side faces in one material-generation batch. Splitting them changes
  // procedural roof detail geometry because pattern cell sequencing is per mesh node.
  emitRoofingFaces({
    faces: sideAndTopFaces,
    faceCategory: FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY,
    threeLayers,
    materialSlot: roofingMaterial,
    materialSrc: topMaterialSrc,
    entityHandleForSelectingReportingUnit,
    exportComponentName: "Main/Top",
    emitter,
  });
}

/**
 * Generate BuildingGeometry for gable wall surfaces (when not using RoofWallSystem).
 *
 * This handles:
 * - Exterior wall faces from all four sides (START, END, LEFT, RIGHT)
 * - Side roof wall faces
 * - Vaulted roof cap faces
 * - Hole trim faces (window/door openings in gable walls)
 */
export function getGableWallsGeometries(
  roof: GeneratedRoof,
  exteriorPalette: ExteriorPalette,
  exteriorWallMaterial: ExteriorMaterial,
  entityTypes: Set<FaceCategory>,
  entityHandleForSelectingReportingUnit: DocEntityHandle,
  emitter: BuildingGeometryEmitter,
): void {
  const { wallFaces, wallMaterial = exteriorWallMaterial } = roof;

  const { startWallFaces, endWallFaces, vaultedRoofCapFaces } = wallFaces;
  const { leftHoleFaces: startFrontHoleFaces, rightHoleFaces: startBackHoleFaces } = startWallFaces;
  const { leftHoleFaces: endFrontHoleFaces, rightHoleFaces: endBackHoleFaces } = endWallFaces;

  const threeLayers = buildExteriorLayers();

  // Collect gable wall exterior faces
  if (entityTypes.has(FaceCategory.ROOF_WALL_EXTERIOR)) {
    const gableWallExteriorFaces: Face[] = [];

    // Exterior faces from all four sides
    gableWallExteriorFaces.push(...getExteriorRoofWallFaces(roof, RoofSide.START));
    gableWallExteriorFaces.push(...getExteriorRoofWallFaces(roof, RoofSide.END));
    gableWallExteriorFaces.push(...getExteriorRoofWallFaces(roof, RoofSide.LEFT));
    gableWallExteriorFaces.push(...getExteriorRoofWallFaces(roof, RoofSide.RIGHT));

    // Side faces from all four sides
    gableWallExteriorFaces.push(...getSideRoofWallFaces(roof, RoofSide.START));
    gableWallExteriorFaces.push(...getSideRoofWallFaces(roof, RoofSide.END));
    gableWallExteriorFaces.push(...getSideRoofWallFaces(roof, RoofSide.LEFT));
    gableWallExteriorFaces.push(...getSideRoofWallFaces(roof, RoofSide.RIGHT));

    // Vaulted roof cap faces
    for (const vaultedRoofCapFace of vaultedRoofCapFaces) {
      const { leftFaces } = vaultedRoofCapFace;
      gableWallExteriorFaces.push(...leftFaces);
    }

    if (gableWallExteriorFaces.length > 0) {
      const materialSrc = materialSrcFromPalette(exteriorPalette, wallMaterial);
      emitter.emit({
        geom: faceGroupNew(gableWallExteriorFaces),
        faceCategory: FaceCategory.ROOF_WALL_EXTERIOR,
        threeLayers,
        materialSlot: wallMaterial,
        materialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
      });
    }
  }

  // Collect gable wall trim faces (window/door hole trim)
  if (entityTypes.has(FaceCategory.ROOF_WALL_TRIM)) {
    const gableWallTrimFaces: Face[] = [];

    gableWallTrimFaces.push(...startFrontHoleFaces);
    gableWallTrimFaces.push(...startBackHoleFaces);
    gableWallTrimFaces.push(...endFrontHoleFaces);
    gableWallTrimFaces.push(...endBackHoleFaces);

    if (gableWallTrimFaces.length > 0) {
      const trimMaterial: ExteriorMaterial = "trimExterior0";
      const materialSrc = materialSrcFromPalette(exteriorPalette, trimMaterial);
      emitter.emit({
        geom: faceGroupNew(gableWallTrimFaces),
        faceCategory: FaceCategory.ROOF_WALL_TRIM,
        threeLayers,
        materialSlot: trimMaterial,
        materialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
        entityHandleForSelectingReportingUnit,
      });
    }
  }
}

/**
 * Generate BuildingGeometry for a roof wall system (when using RoofWallSystem).
 *
 * This uses the wall geometry helpers with FACADE_WALL_EXTERIOR category
 * to match the original behavior in collectBuildingFaces.
 */
function getRoofWallSystemGeometries(
  roofWallSystem: RoofWallSystem,
  building: BuildingForRoofGeometries,
  settings: HomeSettingsForRoofGeometries,
  options: BuildingGeometryOptions,
  entityTypes: Set<FaceCategory>,
  entityHandleForSelectingReportingUnit: DocEntityHandle,
  emitter: BuildingGeometryEmitter,
): void {
  const { datumAbstractions, enabledFeatures } = building;
  const { wallSystem, levelId, levels, levelsAttributes } = roofWallSystem;
  const roofWallSweeps = building.wallSweeps[levelId];

  const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(levels, levelsAttributes, datumAbstractions);
  const wallFinishFaces =
    enabledFeatures.wallPlanes || enabledFeatures.collectWallFinishFaces2
      ? collectWallFinishFaces2({
          wallDatumAbstractionProvider,
          wallSystem,
          wallSweeps: roofWallSweeps,
          wallCatalogSettings: settings.wallCatalog,
          mirror: building.mirror,
        })
      : collectWallFinishFaces({
          wallDatumAbstractionProvider,
          wallSystem,
          wallSweeps: roofWallSweeps,
          interiorPalette: building.interiorPalette,
          exteriorPalette: building.exteriorPalette,
          wallSettings: settings.wall,
          wallCatalogSettings: settings.wallCatalog,
          mirror: building.mirror,
          enableLegacyDepthOffsets: options.mode !== BuildingGeometryMode.EXPORT,
        });

  // Use FACADE_WALL_EXTERIOR to match original behavior in collectBuildingFaces
  getWallFinishGeometries(
    levelId,
    wallFinishFaces,
    building,
    settings,
    options,
    entityTypes,
    { exteriorFaceCategory: FaceCategory.FACADE_WALL_EXTERIOR },
    entityHandleForSelectingReportingUnit,
    emitter,
  );

  getWallSweepGeometries(
    levelId,
    wallFinishFaces,
    building,
    entityTypes,
    entityHandleForSelectingReportingUnit,
    emitter,
  );
}

/**
 * Generate BuildingGeometry for dormer trim elements (corner boards, wall base boards).
 */
function getDormerTrimGeometries(
  trims: FaceArrayViewEntity[],
  faceCategory: FaceCategory,
  exteriorPalette: ExteriorPalette,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined,
  emitter: BuildingGeometryEmitter,
): void {
  const threeLayers = buildExteriorLayers();

  for (const { faces, descriptor, entityId, material = "trimExterior0" } of trims) {
    if (faces.length === 0) {
      continue;
    }

    const materialSrc = materialSrcFromPalette(exteriorPalette, material);

    emitter.emit({
      geom: faceGroupNew(faces),
      faceCategory,
      threeLayers,
      entityId,
      descriptor,
      materialSlot: material,
      materialSrc,
      uvGen: UVGeneration.GLOBAL_RECT_UV,
      entityHandleForSelectingReportingUnit,
    });
  }
}

/**
 * Generate BuildingGeometry for a roof dormer.
 *
 * This handles:
 * - Corner boards (CORNER_BOARD)
 * - Wall base boards (ROOF_WALL_BASE_BOARD)
 * - Dormer wall faces (ROOF_DORMER_WALL)
 */
export function getRoofDormerGeometries(
  dormer: RoofDormer,
  exteriorPalette: ExteriorPalette,
  exteriorWallMaterial: ExteriorMaterial,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
): void {
  const { wall, cornerBoards, wallBaseBoards, wallMaterial = exteriorWallMaterial } = dormer;
  // Attribute all dormer geometry to the dormer; reporting-unit resolution walks up to the roof.
  const { entityHandleForSelectingReportingUnit } = dormer;

  // Corner boards
  if (entityTypes.has(FaceCategory.CORNER_BOARD)) {
    getDormerTrimGeometries(
      cornerBoards,
      FaceCategory.CORNER_BOARD,
      exteriorPalette,
      entityHandleForSelectingReportingUnit,
      emitter,
    );
  }

  // Wall base boards
  if (entityTypes.has(FaceCategory.ROOF_WALL_BASE_BOARD)) {
    getDormerTrimGeometries(
      wallBaseBoards,
      FaceCategory.ROOF_WALL_BASE_BOARD,
      exteriorPalette,
      entityHandleForSelectingReportingUnit,
      emitter,
    );
  }

  // Dormer wall
  if (entityTypes.has(FaceCategory.ROOF_DORMER_WALL)) {
    const threeLayers = buildExteriorLayers();
    const materialSrc = materialSrcFromPalette(exteriorPalette, wallMaterial);

    if (wall.length > 0) {
      emitter.emit({
        geom: faceGroupNew(wall),
        faceCategory: FaceCategory.ROOF_DORMER_WALL,
        threeLayers,
        materialSlot: wallMaterial,
        materialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
        pickHandle: dormer.pickHandle,
        entityHandleForSelectingReportingUnit,
      });
    }
  }
}

/**
 * Generate BuildingGeometry for a single skylight unit.
 *
 * This handles:
 * - Exterior faces (SKYLIGHT_EXTERIOR)
 * - Decoration faces (SKYLIGHT_DECORATION)
 * - Glass faces (SKYLIGHT_GLASS)
 */
export function getSkylightGeometries(
  skylightUnit: SkylightUnit,
  exteriorPalette: ExteriorPalette,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
): void {
  // Attribute all skylight geometry to the skylight; reporting-unit resolution walks up to the roof.
  const { entityHandleForSelectingReportingUnit } = skylightUnit;

  // Skylight exterior faces
  if (entityTypes.has(FaceCategory.SKYLIGHT_EXTERIOR)) {
    getExteriorGroupsGeometries({
      levelId: undefined,
      groups: [skylightUnit.exterior],
      palette: exteriorPalette,
      faceCategory: FaceCategory.SKYLIGHT_EXTERIOR,
      entityHandleForSelectingReportingUnit,
      emitter,
    });
  }

  // Skylight decoration faces
  if (entityTypes.has(FaceCategory.SKYLIGHT_DECORATION)) {
    getExteriorGroupsGeometries({
      levelId: undefined,
      groups: [skylightUnit.decorations],
      palette: exteriorPalette,
      faceCategory: FaceCategory.SKYLIGHT_DECORATION,
      entityHandleForSelectingReportingUnit,
      emitter,
    });
  }

  // Skylight glass
  if (entityTypes.has(FaceCategory.SKYLIGHT_GLASS)) {
    const { glass } = skylightUnit;
    if (glass.length > 0) {
      const threeLayers = buildExteriorLayers();
      const materialSlot: ExteriorMaterial = "glass0";
      const materialSrc = materialSrcFromPalette(exteriorPalette, materialSlot);
      emitter.emit({
        geom: faceGroupNew(glass),
        faceCategory: FaceCategory.SKYLIGHT_GLASS,
        threeLayers,
        materialSlot,
        materialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
        entityHandleForSelectingReportingUnit,
      });
    }
  }
}

export function getSubRoofGeometries(
  roof: GeneratedRoof,
  subRoofHandle: DocEntityHandle,
  entityTypes: Set<FaceCategory>,
  building: BuildingForRoofGeometries,
  settings: HomeSettingsForRoofGeometries,
  options: BuildingGeometryOptions,
  emitter: BuildingGeometryEmitter,
  renderLevelId: string,
): void {
  const { exteriorPalette } = building;
  const { exteriorWallMaterial } = settings.wall;

  const {
    friezeBoards,
    fasciaBoards,
    fasciaTopTrims,
    soffits,
    bellyBandBoards,
    wallBaseBoards,
    exposedRafters,
    gableVentFaces,
    ridgeVentFaces,
    ridgeCapFaces,
    roofSettings,
    pickHandle,
  } = roof;

  const { roofRidgeRoofingCapMaterial = "roofTrim0" } = roofSettings;

  const entityHandleForSelectingReportingUnit = subRoofHandle;

  const isExporting = options.mode === BuildingGeometryMode.EXPORT;

  // Create a wrapper emitter that automatically adds levelId and aboveLevel to all geometry
  // This ensures roof geometry is properly grouped for interior view visibility control
  const roofEmitter = createWrappedEmitter(emitter, {
    levelId: renderLevelId,
    aboveLevel: true,
  });

  // Roof wall geometry - use wall system if available, otherwise use gable walls
  // Note: Wall system geometry uses roofEmitter to inherit levelId/aboveLevel
  if (wallFaceCategories.some((category) => entityTypes.has(category))) {
    if (roof.roofWallSystem) {
      getRoofWallSystemGeometries(
        roof.roofWallSystem,
        building,
        settings,
        options,
        entityTypes,
        entityHandleForSelectingReportingUnit,
        roofEmitter,
      );
    } else {
      getGableWallsGeometries(
        roof,
        exteriorPalette,
        exteriorWallMaterial,
        entityTypes,
        entityHandleForSelectingReportingUnit,
        roofEmitter,
      );
    }
  }

  // Roof top surfaces
  if (entityTypes.has(FaceCategory.ROOF_TOP)) {
    getRoofTopGeometries(roof, exteriorPalette, isExporting, entityHandleForSelectingReportingUnit, roofEmitter);
  }

  // Roof bottom surfaces
  if (entityTypes.has(FaceCategory.ROOF_BOTTOM)) {
    getRoofBottomGeometries(roof, exteriorPalette, isExporting, entityHandleForSelectingReportingUnit, roofEmitter);
  }

  // Export-specific customizable geometry
  if (entityTypes.has(FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY)) {
    getRoofTopCustomizableExportGeometries(roof, exteriorPalette, entityHandleForSelectingReportingUnit, roofEmitter);
  }

  // Ridge vents and caps
  if (entityTypes.has(FaceCategory.ROOF_RIDGE_VENT)) {
    roofEmitter.beginGroup?.("Roof Ridge Vents");
    getRoofRidgeVentGeometries(
      ridgeVentFaces,
      exteriorPalette,
      pickHandle,
      entityHandleForSelectingReportingUnit,
      roofEmitter,
    );
    roofEmitter.endGroup?.("Roof Ridge Vents");
  }

  if (entityTypes.has(FaceCategory.ROOF_RIDGE_CAP)) {
    roofEmitter.beginGroup?.("Roof Ridge Caps");
    getRoofRidgeCapGeometries(
      ridgeCapFaces,
      exteriorPalette,
      roofRidgeRoofingCapMaterial,
      pickHandle,
      entityHandleForSelectingReportingUnit,
      roofEmitter,
    );
    roofEmitter.endGroup?.("Roof Ridge Caps");
  }

  roofEmitter.beginGroup?.("Roof Trim");

  // Zip the trim entities and the face categories into pairs for easier iteration
  const trimEntities = [
    { entity: bellyBandBoards, faceCategory: FaceCategory.ROOF_BELLY_BAND_BOARD },
    { entity: wallBaseBoards, faceCategory: FaceCategory.ROOF_WALL_BASE_BOARD },
    { entity: gableVentFaces, faceCategory: FaceCategory.ROOF_GABLE_VENT },
    { entity: exposedRafters, faceCategory: FaceCategory.ROOF_EXPOSED_RAFTER },
    { entity: soffits, faceCategory: FaceCategory.ROOF_SOFFIT },
    { entity: fasciaBoards, faceCategory: FaceCategory.ROOF_FASCIA_BOARD },
    { entity: fasciaTopTrims, faceCategory: FaceCategory.ROOF_FASCIA_TOP_TRIM },
    { entity: friezeBoards, faceCategory: FaceCategory.ROOF_FRIEZE_BOARD },
  ];

  for (const { entity, faceCategory } of trimEntities) {
    if (entityTypes.has(faceCategory)) {
      getRoofTrimGeometries(entity, faceCategory, exteriorPalette, entityHandleForSelectingReportingUnit, roofEmitter);
    }
  }

  roofEmitter.endGroup?.("Roof Trim");

  // Emit roof props
  if (building.propCatalog && roof.props) {
    const { propCatalog } = building;
    const threeLayers = buildExteriorLayers();

    for (const prop of roof.props) {
      const propAttributes = propCatalog[prop.src];
      const faceCategory = faceCategoryFromPropCategory(propAttributes?.category);

      emitBuildingProp(emitter, prop, {
        threeLayers,
        faceCategory,
        propCatalog,
        levelId: renderLevelId,
        aboveLevel: true,
        pickHandle,
        entityHandleForSelectingReportingUnit,
      });
    }
  }
}

function getRoofTopAABB3(roof: GeneratedRoof): AABB3 | undefined {
  const roofTopFaces = roof.roofTopPlates.map((roofTopPlate) => extrusionToBRep(roofTopPlate).faces[1]);
  return roofTopFaces.length > 0 ? facesAABB3(roofTopFaces) : undefined;
}

export function getRoofGeometries(
  building: BuildingForRoofGeometries,
  settings: HomeSettingsForRoofGeometries,
  options: BuildingGeometryOptions,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
): void {
  const { roofSystems, dormers, skylightUnits, exteriorPalette } = building;
  const { exteriorWallMaterial } = settings.wall;
  const resolveRoofRenderLevel = createRoofRenderLevelResolver(building.levels, building.blocks);

  // Process all roof systems and sub-roofs
  for (const roofId in roofSystems) {
    const roof = roofSystems[roofId];
    for (const subRoofId in roof.roofs) {
      const subRoof = roof.roofs[subRoofId];
      const subRoofHandle: DocEntityHandle = { type: "ROOF", id: subRoofId };
      const roofAabb = getRoofTopAABB3(subRoof);
      const renderLevelId = roofAabb ? resolveRoofRenderLevel(roofAabb) ?? subRoof.levelId : subRoof.levelId;
      emitter.beginGroup?.("Roof");
      getSubRoofGeometries(subRoof, subRoofHandle, entityTypes, building, settings, options, emitter, renderLevelId);
      emitter.endGroup?.("Roof");
    }
  }

  // Process all dormers
  for (const dormerId in dormers) {
    emitter.beginGroup?.("Roof");
    getRoofDormerGeometries(dormers[dormerId], exteriorPalette, exteriorWallMaterial, entityTypes, emitter);
    emitter.endGroup?.("Roof");
  }

  // Process all skylights
  for (const skylightId in skylightUnits) {
    emitter.beginGroup?.("Skylight");
    getSkylightGeometries(skylightUnits[skylightId], exteriorPalette, entityTypes, emitter);
    emitter.endGroup?.("Skylight");
  }
}
